import type { EndReason, RunResult } from './types';

/**
 * 우승 기준 C — 단일 정렬식 (SEAL §2.6 · ADR-016 개정).
 *   ① sealed desc  ② solved desc  ③ lastSolveTick asc (solved 0 이면 미적용)
 *   tick-limit · quit · abandoned 는 실패 계층 최하위 고정.
 *
 * 생존 틱을 키로 두지 않는 이유: 회피 파일럿(408틱·해결 0)이 브루트포서(265틱·해결 2.32)보다
 * 상위가 된다 — 아무것도 안 한 판이 순위를 받는다.
 */
export interface RankKey {
  readonly sealed: boolean;
  readonly solved: number;
  readonly lastSolveTick: number; // -1 = 없음
  readonly endReason: EndReason;
}

const BOTTOM: ReadonlySet<EndReason> = new Set(['tick-limit', 'quit', 'abandoned']);

export function isBottom(r: RankKey): boolean {
  return BOTTOM.has(r.endReason);
}

export function rankKeyOf(r: RunResult): RankKey {
  return {
    sealed: r.sealed,
    solved: r.solved,
    lastSolveTick: r.lastSolveTick,
    endReason: r.endReason,
  };
}

/** 음수 = a 가 더 낫다. 0 = 동점. 반대칭·추이성을 만족하는 전순서다. */
export function compareRank(a: RankKey, b: RankKey): number {
  const ab = isBottom(a);
  const bb = isBottom(b);
  if (ab !== bb) return ab ? 1 : -1;
  if (ab) return 0;
  if (a.sealed !== b.sealed) return a.sealed ? -1 : 1;
  if (a.solved !== b.solved) return b.solved - a.solved;
  if (a.solved === 0) return 0; // 3순위 미적용
  return a.lastSolveTick - b.lastSolveTick;
}

export function betterRank(a: RankKey, b: RankKey): boolean {
  return compareRank(a, b) < 0;
}

export type Grade = 'S' | 'A' | 'B' | 'C' | 'D' | 'E' | 'F';

/** 성공: 명목 < 65초 S · < 80초 A · 그 외 B / 실패: 해결 6~7 C · 3~5 D · 1~2 E · 0 F. 최하위 계층은 F. */
export function gradeOf(r: RunResult): Grade {
  if (isBottom(rankKeyOf(r))) return 'F';
  if (r.sealed) {
    const sec = r.sealTicks < 0 ? Number.POSITIVE_INFINITY : (r.sealTicks * 135) / 1000;
    return sec < 65 ? 'S' : sec < 80 ? 'A' : 'B';
  }
  if (r.solved >= 6) return 'C';
  if (r.solved >= 3) return 'D';
  if (r.solved >= 1) return 'E';
  return 'F';
}
